import messageService from '@/services/api/message.service.js'

/**
 * Handles incoming new messages from the socket.
 * Updates DB and Pinia state using a more streamlined approach.
 * @param {Object} state - Pinia store state (contains pendingOptimisticMessages).
 * @param {Object} db - IndexedDB utility instance.
 * @param {Object} message - The incoming message object.
 * @param {boolean} isSender - Whether the message is from the current user initially.
 */
export async function handleNewMessage(state, db, message, isSender) {
    console.log('Handler helper: New message', message, isSender)

    let successfullyReplacedInUI = false // Track if UI splice occurred
    let tempLookupFailed = false // Track if tempId received but not found in pending map

    // --- Step 1: Handle Optimistic State & Potential UI Replacement ---
    if (isSender && message.tempId) {
        const tempId = message.tempId
        const pendingMessage = state.pendingOptimisticMessages[tempId]

        if (pendingMessage) {
            console.log(
                `Found pending optimistic message ${tempId} for replacement.`
            )
            // 1a. Remove from pending map
            delete state.pendingOptimisticMessages[tempId]

            // 1b. Revoke Blob URL if it exists in the global cache
            if (
                pendingMessage.contentType !== 'text' &&
                window._pendingBlobURLs &&
                window._pendingBlobURLs[tempId]
            ) {
                console.log(
                    `Revoking blob URL for replaced temp ${pendingMessage.contentType} ${tempId}`
                )
                URL.revokeObjectURL(window._pendingBlobURLs[tempId])
                delete window._pendingBlobURLs[tempId] // Clean up cache entry
            }

            // 1c. Attempt to replace in Current UI (only if conversation is active)
            const isCurrentForReplace =
                state.currentConversation?._id === message.conversationId
            if (isCurrentForReplace) {
                const tempMessageIndex = state.currentMessages.findIndex(
                    (msg) => msg._id === tempId // Find by tempId (_id of optimistic message)
                )
                if (tempMessageIndex > -1) {
                    // Replace the temp message with the real one in the current view
                    state.currentMessages.splice(tempMessageIndex, 1, message)
                    successfullyReplacedInUI = true // Mark that UI splice happened
                    console.log(
                        `Replaced temp message ${tempId} in currentMessages with real message ${message._id}`
                    )
                } else {
                    // This could happen if messages were reloaded or cleared while upload was pending
                    console.warn(
                        `Temp message ${tempId} was in pending map but not found in currentMessages array for conversation ${message.conversationId}. Final message will be added if conversation is current.`
                    )
                }
            } else {
                console.log(
                    `Temp message ${tempId} replaced, but conversation ${message.conversationId} is not current.`
                )
            }
        } else {
            // Temp ID received from backend, but not found in our pending map.
            console.warn(
                `Received message with tempId ${message.tempId} but it was not found in the pendingOptimisticMessages map.`
            )
            tempLookupFailed = true // Flag this failure
        }
    } // --- End Optimistic Handling ---

    // --- Step 2: Update Current UI if Necessary ---
    const isCurrent = state.currentConversation?._id === message.conversationId
    if (isCurrent && !successfullyReplacedInUI) {
        // Add message to currentMessages if it wasn't a successful UI replacement
        // Covers: incoming messages, sender messages where temp lookup failed, or splice failed.
        if (!state.currentMessages.find((m) => m._id === message._id)) {
            state.currentMessages.push(message)
            console.log(`Added message ${message._id} to currentMessages.`)
        }
    }

    // --- Step 3: Update Database (Always) ---
    // Use putMessages which handles updates/inserts
    db.putMessages([message]).catch((err) =>
        console.error('DB Error putting final message:', err)
    )
    // Update the conversation lastMessage in IndexedDB as well (fire-and-forget)
    db.updateConversationLastMessage(message.conversationId, message).catch(
        (err) =>
            console.error('DB Error updating conversation last message:', err)
    )

    // --- Step 4: Update Conversation List Last Message (Always) ---
    const convoIndex = state.conversations.findIndex(
        (c) => c._id === message.conversationId
    )
    if (convoIndex > -1) {
        // Ensure the lastMessage reflects the *final* confirmed message
        state.conversations[convoIndex].lastMessage = message
    } else {
        console.warn(
            `Conversation ${message.conversationId} not found in store for new message ${message._id}`
        )
    }

    // --- Step 5: Acknowledge Receipt/Read (If Applicable) ---
    // Acknowledge if it's effectively an incoming message (not sender OR temp lookup failed)
    if (!isSender || tempLookupFailed) {
        console.log(
            'Handling message as received from other party (or failed temp lookup). Is current?',
            isCurrent
        )
        // Send 'received' status update via API
        messageService.markMessagesAsReceived([message._id]).catch((err) => {
            console.error(
                `API Error marking message ${message._id} as received:`,
                err
            )
        })

        // If it's the current conversation, also send 'read' status update
        if (isCurrent) {
            console.log(
                `Marking message ${message._id} as read via API because it's the current conversation.`
            )
            messageService.markMessagesAsRead([message._id]).catch((err) => {
                console.error(
                    `API Error marking message ${message._id} as read:`,
                    err
                )
            })
        }
    }
}
